import {
  Circle,
  Ellipse,
  Rect,
  RegularPolygon,
  Star,
} from 'react-konva';
import type { NodeShapeName } from '../../types/project';

// ------------------------------------------------------------
// Konva node body by shape name
// ------------------------------------------------------------

interface NodeBodyShapeProps {
  shape: NodeShapeName;
  radius: number;
  fill: string;
  stroke: string;
  strokeWidth: number;
  shadowColor?: string;
  shadowBlur?: number;
  shadowOpacity?: number;
  shadowOffsetY?: number;
}

export default function NodeBodyShape({
  shape,
  radius,
  fill,
  stroke,
  strokeWidth,
  shadowColor,
  shadowBlur = 0,
  shadowOpacity = 0,
  shadowOffsetY = 0,
}: NodeBodyShapeProps) {
  const common = {
    fill,
    stroke,
    strokeWidth,
    shadowColor,
    shadowBlur,
    shadowOpacity,
    shadowOffsetY,
    perfectDrawEnabled: false as const,
  };

  switch (shape) {
    case 'square':
      return (
        <Rect
          x={-radius}
          y={-radius}
          width={radius * 2}
          height={radius * 2}
          {...common}
        />
      );
    case 'rounded_square':
      return (
        <Rect
          x={-radius}
          y={-radius}
          width={radius * 2}
          height={radius * 2}
          cornerRadius={radius * 0.28}
          {...common}
        />
      );
    case 'rectangle':
      return (
        <Rect
          x={-radius}
          y={-radius * 0.65}
          width={radius * 2}
          height={radius * 1.3}
          cornerRadius={radius * 0.12}
          {...common}
        />
      );
    case 'triangle':
      return (
        <RegularPolygon
          sides={3}
          radius={radius}
          rotation={-90}
          {...common}
        />
      );
    case 'diamond':
      return (
        <RegularPolygon
          sides={4}
          radius={radius}
          rotation={45}
          {...common}
        />
      );
    case 'hexagon':
      return <RegularPolygon sides={6} radius={radius} {...common} />;
    case 'pentagon':
      return (
        <RegularPolygon
          sides={5}
          radius={radius}
          rotation={-90}
          {...common}
        />
      );
    case 'octagon':
      return <RegularPolygon sides={8} radius={radius} {...common} />;
    case 'star':
      return (
        <Star
          numPoints={5}
          innerRadius={radius * 0.48}
          outerRadius={radius}
          {...common}
        />
      );
    case 'ellipse':
      return (
        <Ellipse
          radiusX={radius}
          radiusY={radius * 0.62}
          {...common}
        />
      );
    case 'cross': {
      const arm = radius * 0.34;
      const span = radius * 1.05;
      return (
        <>
          <Rect
            x={-arm / 2}
            y={-span}
            width={arm}
            height={span * 2}
            cornerRadius={arm * 0.2}
            {...common}
          />
          <Rect
            x={-span}
            y={-arm / 2}
            width={span * 2}
            height={arm}
            cornerRadius={arm * 0.2}
            {...common}
          />
        </>
      );
    }
    case 'circle':
    default:
      return <Circle radius={radius} {...common} />;
  }
}

/** Small SVG preview for palette / pickers. */
export function NodeShapePreview({
  shape,
  color,
  size = 16,
}: {
  shape: NodeShapeName;
  color: string;
  size?: number;
}) {
  const r = size / 2 - 1;
  const cx = size / 2;
  const cy = size / 2;

  const stroke = 'rgba(255,255,255,0.2)';

  switch (shape) {
    case 'square':
      return (
        <rect
          x={cx - r}
          y={cy - r}
          width={r * 2}
          height={r * 2}
          fill={color}
          stroke={stroke}
          strokeWidth={0.75}
        />
      );
    case 'rounded_square':
      return (
        <rect
          x={cx - r}
          y={cy - r}
          width={r * 2}
          height={r * 2}
          rx={r * 0.28}
          fill={color}
          stroke={stroke}
          strokeWidth={0.75}
        />
      );
    case 'rectangle':
      return (
        <rect
          x={cx - r}
          y={cy - r * 0.65}
          width={r * 2}
          height={r * 1.3}
          rx={r * 0.12}
          fill={color}
          stroke={stroke}
          strokeWidth={0.75}
        />
      );
    case 'triangle':
      return (
        <polygon
          points={`${cx},${cy - r} ${cx + r * 0.87},${cy + r * 0.5} ${cx - r * 0.87},${cy + r * 0.5}`}
          fill={color}
          stroke={stroke}
          strokeWidth={0.75}
        />
      );
    case 'diamond':
      return (
        <polygon
          points={`${cx},${cy - r} ${cx + r},${cy} ${cx},${cy + r} ${cx - r},${cy}`}
          fill={color}
          stroke={stroke}
          strokeWidth={0.75}
        />
      );
    case 'hexagon': {
      const pts = Array.from({ length: 6 }, (_, i) => {
        const a = (Math.PI / 3) * i - Math.PI / 6;
        return `${cx + Math.cos(a) * r},${cy + Math.sin(a) * r}`;
      }).join(' ');
      return (
        <polygon points={pts} fill={color} stroke={stroke} strokeWidth={0.75} />
      );
    }
    case 'pentagon': {
      const pts = Array.from({ length: 5 }, (_, i) => {
        const a = ((Math.PI * 2) / 5) * i - Math.PI / 2;
        return `${cx + Math.cos(a) * r},${cy + Math.sin(a) * r}`;
      }).join(' ');
      return (
        <polygon points={pts} fill={color} stroke={stroke} strokeWidth={0.75} />
      );
    }
    case 'octagon': {
      const pts = Array.from({ length: 8 }, (_, i) => {
        const a = (Math.PI / 4) * i - Math.PI / 8;
        return `${cx + Math.cos(a) * r},${cy + Math.sin(a) * r}`;
      }).join(' ');
      return (
        <polygon points={pts} fill={color} stroke={stroke} strokeWidth={0.75} />
      );
    }
    case 'star': {
      const pts = Array.from({ length: 10 }, (_, i) => {
        const rad = i % 2 === 0 ? r : r * 0.48;
        const a = (Math.PI / 5) * i - Math.PI / 2;
        return `${cx + Math.cos(a) * rad},${cy + Math.sin(a) * rad}`;
      }).join(' ');
      return (
        <polygon points={pts} fill={color} stroke={stroke} strokeWidth={0.75} />
      );
    }
    case 'ellipse':
      return (
        <ellipse
          cx={cx}
          cy={cy}
          rx={r}
          ry={r * 0.62}
          fill={color}
          stroke={stroke}
          strokeWidth={0.75}
        />
      );
    case 'cross':
      return (
        <>
          <rect
            x={cx - r * 0.17}
            y={cy - r}
            width={r * 0.34}
            height={r * 2}
            rx={r * 0.08}
            fill={color}
            stroke={stroke}
            strokeWidth={0.75}
          />
          <rect
            x={cx - r}
            y={cy - r * 0.17}
            width={r * 2}
            height={r * 0.34}
            rx={r * 0.08}
            fill={color}
            stroke={stroke}
            strokeWidth={0.75}
          />
        </>
      );
    default:
      return (
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill={color}
          stroke={stroke}
          strokeWidth={0.75}
        />
      );
  }
}
